// auth/RequireAuth.tsx
import { Navigate, useLocation } from "react-router-dom";
import { tokenStore } from "../utils/tokenStore";
import { parseJwt } from "./jwtUtils";
import { refreshAccessToken } from "../utils/authClient";
import { useEffect, useState } from "react";
import { useCountdownTo } from "../hooks/useCountdownTo";

type RequireAuthProps = {
  children: React.ReactNode;
  requiredRole?: string;
};

export default function RequireAuth({
  children,
  requiredRole,
}: RequireAuthProps) {
  const location = useLocation();
  const [status, setStatus] = useState<"checking" | "ok" | "unauthorized">(
    "checking"
  );

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      var { token } = tokenStore.get();

      // không có token → thử refresh
      if (!token) {
        const newToken = await refreshAccessToken();

        if (!newToken) {
          if (isMounted) setStatus("unauthorized");
          return;
        }
        token = newToken;
      }

      try {
        const payload = parseJwt(token);

        const now = Date.now() / 1000;

        const hasRole = (role: string) => {
          if (!requiredRole) return true;
          const roles: string[] = Array.isArray(payload.role)
            ? payload.role
            : payload.role
            ? [payload.role]
            : [];
          return roles.includes(requiredRole);
        };

        // token còn hạn
        if (!payload.exp || payload.exp > now) {
          if (!hasRole(requiredRole ?? "")) {
            if (isMounted) setStatus("unauthorized");
            return;
          }

          if (isMounted) setStatus("ok");
          return;
        }

        // token hết hạn → thử refresh
        const newToken = await refreshAccessToken();

        if (!newToken) {
          if (isMounted) setStatus("unauthorized");
          return;
        }

        // parse lại token mới để check role
        const newPayload = parseJwt(newToken);

        if (requiredRole) {
          const roles: string[] = Array.isArray(newPayload.role)
            ? newPayload.role
            : newPayload.role
            ? [newPayload.role]
            : [];

          if (!roles.includes(requiredRole)) {
            if (isMounted) setStatus("unauthorized");
            return;
          }
        }

        if (isMounted) setStatus("ok");
      } catch {
        tokenStore.clear();
        if (isMounted) setStatus("unauthorized");
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, requiredRole]);

  // đang check: có thể trả null, skeleton, hoặc spinner
  if (status === "checking") {
    return null;
    // hoặc:
    // return <div>Đang kiểm tra đăng nhập...</div>;
  }

  if (status === "unauthorized") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
